import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMenu } from '../../contexts/MenuContext';
import { useLocation } from '../../contexts/LocationContext';
import MenuItemForm from './MenuItemForm';
import MenuCopyModal from './MenuCopyModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiImage, FiDollarSign, FiSettings, FiCopy, FiFilter, FiMapPin } = FiIcons;

const MenuManagement = () => {
  const { menuItems, menuCategories, deleteMenuItem, duplicateMenuItem } = useMenu();
  const { locations, selectedLocation } = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState(selectedLocation?.id || 'all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showCopyModal, setShowCopyModal] = useState(false);

  // Filter items by category and location
  const filteredItems = menuItems.filter(item => {
    const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory || item.category_id === selectedCategory;
    const locationMatch = selectedLocationFilter === 'all' || 
      !item.location_ids || 
      item.location_ids.length === 0 || 
      item.location_ids.includes(selectedLocationFilter);
    return categoryMatch && locationMatch;
  });

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDelete = (itemId) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      deleteMenuItem(itemId);
    }
  };

  const handleDuplicate = async (item) => {
    const result = await duplicateMenuItem(item.id);
    if (!result.success) {
      alert(result.error);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const resetSelection = () => {
    setSelectedItems([]);
    setShowCopyModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-playfair text-warm-brown mb-2">Menu Management</h2>
          <p className="text-warm-brown/70">Manage your restaurant menu items</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedItems.length > 0 && (
            <button
              onClick={() => setShowCopyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiCopy} className="text-sm" />
              Copy Selected ({selectedItems.length})
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-sm" />
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiFilter} className="text-warm-brown" />
          <span className="text-warm-brown font-medium">Filters</span>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {/* Location Filter */}
          <div>
            <label className="block text-warm-brown font-medium mb-2">Location</label>
            <select
              value={selectedLocationFilter}
              onChange={(e) => setSelectedLocationFilter(e.target.value)}
              className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-warm-brown font-medium mb-2">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            >
              <option value="all">All Categories</option>
              {menuCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Bulk Actions */}
          <div>
            <label className="block text-warm-brown font-medium mb-2">Bulk Actions</label>
            <button
              onClick={handleSelectAll}
              className="w-full px-4 py-2 border border-warm-brown/20 text-warm-brown rounded-lg hover:bg-warm-brown/5 transition-colors"
            >
              {selectedItems.length === filteredItems.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-warm-brown text-cream'
                : 'bg-warm-brown/10 text-warm-brown hover:bg-warm-brown/20'
            }`}
          >
            All Items ({filteredItems.length})
          </button>
          {menuCategories.map(category => {
            const count = menuItems.filter(item => 
              (item.category === category.id || item.category_id === category.id) &&
              (selectedLocationFilter === 'all' || 
               !item.location_ids || 
               item.location_ids.length === 0 || 
               item.location_ids.includes(selectedLocationFilter))
            ).length;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-colors flex items-center gap-2 ${
                  selectedCategory === category.id
                    ? 'bg-warm-brown text-cream'
                    : 'bg-warm-brown/10 text-warm-brown hover:bg-warm-brown/20'
                }`}
              >
                <span>{category.icon}</span>
                {category.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Menu Items Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 ${
              selectedItems.includes(item.id) ? 'ring-2 ring-warm-brown' : ''
            }`}
          >
            {/* Selection Checkbox */}
            <div className="absolute top-2 left-2 z-10">
              <input
                type="checkbox"
                checked={selectedItems.includes(item.id)}
                onChange={() => handleSelectItem(item.id)}
                className="w-5 h-5 text-warm-brown border-2 border-white rounded focus:ring-warm-brown/50 bg-white/90"
              />
            </div>

            <div className="relative h-48">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              {item.customizable && (
                <div className="absolute top-2 right-2 bg-gold-accent text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <SafeIcon icon={FiSettings} className="text-xs" />
                  Customizable
                </div>
              )}
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-playfair text-warm-brown font-medium">
                  {item.name}
                </h3>
                <span className="text-lg font-bold text-warm-brown flex items-center gap-1">
                  <SafeIcon icon={FiDollarSign} className="text-sm" />
                  {parseFloat(item.price).toFixed(2)}
                </span>
              </div>

              <p className="text-warm-brown/70 text-sm mb-3 line-clamp-2">
                {item.description}
              </p>

              {/* Location Tags */}
              <div className="mb-3">
                {item.location_ids && item.location_ids.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {item.location_ids.map(locationId => {
                      const location = locations.find(loc => loc.id === locationId);
                      return location ? (
                        <span
                          key={locationId}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1"
                        >
                          <SafeIcon icon={FiMapPin} className="text-xs" />
                          {location.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    All Locations
                  </span>
                )}
              </div>

              <div className="mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  menuCategories.find(cat => cat.id === (item.category_id || item.category))?.name
                    ? 'bg-warm-brown/10 text-warm-brown'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {menuCategories.find(cat => cat.id === (item.category_id || item.category))?.name || 'Uncategorized'}
                </span>
              </div>

              {item.ingredients && (
                <div className="mb-4">
                  <p className="text-xs text-warm-brown/60 mb-1">Ingredients:</p>
                  <p className="text-xs text-warm-brown/80">
                    {item.ingredients.slice(0, 3).join(', ')}
                    {item.ingredients.length > 3 && '...'}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="flex-1 bg-warm-brown/10 text-warm-brown py-2 px-3 rounded-lg hover:bg-warm-brown/20 transition-colors flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiEdit2} className="text-sm" />
                  Edit
                </button>
                <button
                  onClick={() => handleDuplicate(item)}
                  className="bg-blue-50 text-blue-600 py-2 px-3 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiCopy} className="text-sm" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="bg-red-50 text-red-600 py-2 px-3 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiImage} className="text-6xl text-warm-brown/30 mx-auto mb-4" />
          <p className="text-warm-brown/70 text-lg">No menu items found</p>
          <p className="text-warm-brown/50 text-sm">
            {selectedLocationFilter !== 'all' 
              ? `No items available for the selected location and filters`
              : 'Add some delicious items to get started!'}
          </p>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <MenuItemForm
          item={editingItem}
          onClose={resetForm}
        />
      )}

      {/* Copy Modal */}
      {showCopyModal && (
        <MenuCopyModal
          selectedItems={selectedItems}
          onClose={resetSelection}
        />
      )}
    </div>
  );
};

export default MenuManagement;